import { Link } from 'react-router-dom';
import { ArrowRight, Smartphone, Star, Download, Wallet, Building2, Sparkles, Headphones } from 'lucide-react';
import { AppStoreBadge, GooglePlayBadge } from './StoreBadges';
import AppPhone from './AppPhone';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { useDevicePlatform } from '../../hooks/useDevicePlatform';

const HIGHLIGHTS = [
  { icon: Building2, text: 'Hand-picked developments across South Africa' },
  { icon: Wallet, text: 'See what you can afford in a minute' },
  { icon: Sparkles, text: 'Ask Cindy, our AI assistant, anything' },
  { icon: Headphones, text: 'Expert articles you can listen to' },
];

export default function AppPromoStrip() {
  const appSection = useScrollReveal<HTMLDivElement>();
  const platform = useDevicePlatform();
  const isMobile = platform === 'ios' || platform === 'android';

  const rating = (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} size={12} className="text-yellow-400 fill-yellow-400" />
      ))}
      <span className="ml-1 text-xs text-gray-400">10,000+ downloads</span>
    </div>
  );

  return (
    <section>
      <div className="relative overflow-hidden bg-gray-900 py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-primary/30" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        <div ref={appSection.ref} className="section-container relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className={`text-white reveal-left ${appSection.isVisible ? 'visible' : ''}`}>
              <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 rounded-full px-4 py-1.5 mb-5 text-sm font-medium text-emerald-300">
                <Smartphone size={14} />
                Free on iOS & Android
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-4">
                Property investing, in your pocket.
              </h2>
              <p className="text-gray-300 text-lg mb-6 max-w-lg leading-relaxed">
                The new PropInvest&#8482; app: everything you need to start building wealth with the bank&rsquo;s money.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-7 max-w-xl">
                {HIGHLIGHTS.map(h => (
                  <li key={h.text} className="flex items-start gap-2.5 text-sm text-gray-200">
                    <span className="w-7 h-7 rounded-lg bg-primary/25 text-emerald-300 flex items-center justify-center shrink-0">
                      <h.icon size={15} />
                    </span>
                    <span className="pt-1">{h.text}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap items-center gap-3 mb-5">
                <div className={platform === 'ios' ? 'animate-subtle-pulse' : ''}>
                  <AppStoreBadge className="h-11" highlighted={isMobile ? platform === 'ios' : undefined} />
                </div>
                <div className={platform === 'android' ? 'animate-subtle-pulse' : ''}>
                  <GooglePlayBadge className="h-11" highlighted={isMobile ? platform === 'android' : undefined} />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <Link
                  to="/mobile-app"
                  className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-5 py-2.5 rounded-lg transition-all shadow-lg shadow-primary/20 hover:shadow-xl hover:-translate-y-0.5 group"
                >
                  <Download size={16} />
                  {isMobile ? 'See what’s inside' : 'Learn More & Download'}
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                {rating}
              </div>
            </div>

            {/* Two phones: Home in front, Explore behind */}
            <div className={`hidden md:flex justify-center reveal-right ${appSection.isVisible ? 'visible' : ''}`}>
              <div className="relative flex items-center">
                <div className="absolute inset-0 bg-primary/25 rounded-full blur-3xl scale-110" />
                <div className="relative z-10">
                  <AppPhone screen="home" className="w-52 lg:w-56" alt="PropInvest app home screen" />
                </div>
                <div className="relative -ml-14 mt-20 rotate-6 opacity-95">
                  <AppPhone screen="qualify" className="w-44 lg:w-48" alt="PropInvest app affordability check" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
